import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Modal, Button } from "react-bootstrap";
import {
  FiTrash2,
  FiDownload,
  FiSend,
  FiCreditCard,
  FiDollarSign,
  FiClock,
  FiEdit2,
  FiEye,
  FiPlus,
  FiX,
  FiCalendar,
} from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import api, { parseWIB } from "../services/api";

const Transactions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);

  // Initialize filter and page from URL params so state is restored on back-navigation
  const [filter, setFilter] = useState(() => ({
    status: searchParams.get("status") || "",
    payment_method: searchParams.get("payment_method") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
    search: searchParams.get("search") || "",
    vehicle_type: searchParams.get("vehicle_type") || "",
  }));
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() =>
    parseInt(searchParams.get("page") || "1", 10),
  );
  const pageRef = useRef(parseInt(searchParams.get("page") || "1", 10));
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;
  const isFirstRender = useRef(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingTx, setPayingTx] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [payAmount, setPayAmount] = useState("");
  const [specialPaymentMethods, setSpecialPaymentMethods] = useState([]);
  const [selectedSpecialPayment, setSelectedSpecialPayment] = useState(null);
  const [payDiscount, setPayDiscount] = useState("");
  const [payDiscountType, setPayDiscountType] = useState("flat");
  const [payVoucherCode, setPayVoucherCode] = useState("");
  const [payValidatedVoucher, setPayValidatedVoucher] = useState(null);
  const [payNotes, setPayNotes] = useState("");
  const [payTxServiceQty, setPayTxServiceQty] = useState(0);
  const [payTxProductSubtotal, setPayTxProductSubtotal] = useState(0);
  const [showWAConfirm, setShowWAConfirm] = useState(false);
  const [doneTx, setDoneTx] = useState(null);
  const [waSending, setWaSending] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editItems, setEditItems] = useState([]); // unified list
  const [editItemType, setEditItemType] = useState("service");
  const [editItemId, setEditItemId] = useState("");
  const [editItemQty, setEditItemQty] = useState(1);
  const [editServices, setEditServices] = useState([]);
  const [editProducts, setEditProducts] = useState([]);
  const [editVehicleId, setEditVehicleId] = useState("");
  const [editVehicles, setEditVehicles] = useState([]);
  const [editPaymentMethod, setEditPaymentMethod] = useState("");

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmLabel: "Ya",
    confirmVariant: "danger",
  });

  const openConfirm = (
    title,
    message,
    onConfirm,
    confirmLabel = "Ya, Hapus",
    confirmVariant = "danger",
  ) => {
    setConfirmModal({
      show: true,
      title,
      message,
      onConfirm,
      confirmLabel,
      confirmVariant,
    });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));
  };

  useEffect(() => {
    api
      .get("/payment-methods/active")
      .then((r) =>
        setSpecialPaymentMethods(Array.isArray(r.data) ? r.data : []),
      )
      .catch(() => {});
  }, []);

  // Sync filter + page to URL (replace so back-button skips these micro-changes)
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    if (filter.payment_method) p.set("payment_method", filter.payment_method);
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    if (filter.search) p.set("search", filter.search);
    if (filter.vehicle_type) p.set("vehicle_type", filter.vehicle_type);
    if (pageRef.current > 1) p.set("page", pageRef.current.toString());
    setSearchParams(p, { replace: true });
  }, [filter, page]); // eslint-disable-line

  // Auto-search: on first render restore page from URL, otherwise reset to page 1
  const searchDebounce = useRef(null);
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (isFirstRender.current) {
      isFirstRender.current = false;
      searchDebounce.current = setTimeout(
        () => loadTransactions(pageRef.current),
        0,
      );
      return () => clearTimeout(searchDebounce.current);
    }
    searchDebounce.current = setTimeout(() => {
      pageRef.current = 1;
      setPage(1);
      loadTransactions(1);
    }, 300);
    return () => clearTimeout(searchDebounce.current);
  }, [filter]); // eslint-disable-line

  const loadTransactions = async (currentPage) => {
    const p = currentPage !== undefined ? currentPage : pageRef.current;
    try {
      const params = { page: p, limit: PAGE_SIZE };
      if (filter.status) params.status = filter.status;
      if (filter.payment_method) params.payment_method = filter.payment_method;
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      if (filter.search) params.search = filter.search;
      if (filter.vehicle_type) params.vehicle_type = filter.vehicle_type;
      const res = await api.get("/transactions", { params });
      // Handle both paginated {transactions, total} and legacy array response
      const data = res.data;
      if (Array.isArray(data)) {
        setTransactions(data);
        setTotalCount(data.length);
      } else {
        setTransactions(data.transactions || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(totalCount / PAGE_SIZE);
    if (newPage < 1 || newPage > maxPage) return;
    pageRef.current = newPage;
    setPage(newPage);
    loadTransactions(newPage);
  };

  const clearFilter = (key) => {
    setFilter((prev) => ({ ...prev, [key]: "" }));
  };

  const doUpdateStatus = async (id, status) => {
    try {
      await api.put(`/transactions/${id}/status`, { status });
      toast.success(`Status diupdate ke ${status}`);
      if (status === "done") {
        const tx = transactions.find((t) => t.id === id);
        if (tx && tx.customer_phone) {
          setDoneTx(tx);
          setShowWAConfirm(true);
        }
      }
      loadTransactions();
    } catch (error) {
      toast.error("Gagal update status");
    }
  };

  const updateStatus = (tx, status) => {
    const label = status === "process" ? "Proses" : "Selesai";
    openConfirm(
      `Ubah Status ke ${label}`,
      `Yakin ubah status transaksi ${tx.invoice_number} ke "${label}"?`,
      () => {
        closeConfirm();
        doUpdateStatus(tx.id, status);
      },
      `Ya, ${label}`,
      "primary",
    );
  };

  // Always use whatsapp-completion endpoint, since modal only appears after marking as done
  const sendWA = async () => {
    if (!doneTx) return;
    setWaSending(true);
    try {
      // Always use whatsapp-completion endpoint
      const preview = await api.get(
        `/transactions/${doneTx.id}/whatsapp-completion`,
      );
      let phone = (preview.data.phone || "").replace(/[^0-9]/g, "");
      // Format nomor ke 62xxx
      if (phone.startsWith("0")) phone = "62" + phone.slice(1);
      else if (phone.startsWith("8")) phone = "62" + phone;
      const message = preview.data.message || "";
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank");
      toast.success("WhatsApp Web dibuka!");
    } catch (error) {
      toast.error("Gagal membuka WhatsApp Web");
    } finally {
      setWaSending(false);
      setShowWAConfirm(false);
      setDoneTx(null);
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = parseWIB(d);
    return (
      dt.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      ", " +
      dt.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  };

  const statusBadge = (status) => {
    const colors = {
      waiting: "warning",
      process: "primary",
      done: "success",
      cancelled: "danger",
    };
    return <Badge bg={colors[status] || "secondary"}>{status}</Badge>;
  };

  const handleDelete = (tx) => {
    openConfirm(
      "Hapus Transaksi",
      `Hapus transaksi ${tx.invoice_number}? Stok produk akan dikembalikan.`,
      async () => {
        closeConfirm();
        try {
          await api.delete(`/transactions/${tx.id}`);
          toast.success("Transaksi berhasil dihapus");
          loadTransactions();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Gagal menghapus transaksi",
          );
        }
      },
    );
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.payment_method)
        params.append("payment_method", filter.payment_method);
      if (filter.date_from) params.append("date_from", filter.date_from);
      if (filter.date_to) params.append("date_to", filter.date_to);
      const res = await api.get(
        `/transactions/export/excel?${params.toString()}`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `transaksi-${filter.date_from || "all"}-${filter.date_to || "all"}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export berhasil");
    } catch (error) {
      toast.error("Gagal export Excel");
    }
  };

  const paymentIcons = {
    cash: <FiDollarSign />,
    debit: <FiCreditCard />,
    credit: <FiCreditCard />,
    qris: <span style={{ fontSize: "0.8em" }}>QR</span>,
    transfer: <span style={{ fontSize: "0.8em" }}>TF</span>,
  };

  const openPayModal = async (tx) => {
    setPayingTx(tx);
    setPayMethod("cash");
    setPayAmount("");
    setSelectedSpecialPayment(null);
    setPayDiscount("");
    setPayDiscountType("flat");
    setPayVoucherCode("");
    setPayValidatedVoucher(null);
    setPayNotes("");
    setPayTxServiceQty(0);
    setPayTxProductSubtotal(0);
    setShowPayModal(true);
    // Fetch details for per-item override calculation
    try {
      const res = await api.get(`/transactions/${tx.id}`);
      const details = res.data?.details || [];
      const svcQty = details
        .filter((d) => d.item_type === "service")
        .reduce((s, d) => s + Number(d.quantity), 0);
      const prodSub = details
        .filter((d) => d.item_type === "product")
        .reduce((s, d) => s + Number(d.subtotal), 0);
      setPayTxServiceQty(svcQty);
      setPayTxProductSubtotal(prodSub);

      // Pre-fill existing discount/voucher from the transaction
      const existingVoucherId = tx.voucher_id || res.data?.voucher_id;
      const existingDiscount = Number(
        tx.discount_amount || res.data?.discount_amount || 0,
      );
      const txSubtotal = Number(tx.subtotal || res.data?.subtotal || 0);

      if (existingVoucherId) {
        try {
          const vRes = await api.get(`/promos/vouchers/${existingVoucherId}`);
          const v = vRes.data;
          const voucherDiscount =
            v.discount_type === "percentage"
              ? txSubtotal * (v.discount_value / 100)
              : Number(v.discount_value);
          const manualDiscount = Math.max(
            0,
            existingDiscount - voucherDiscount,
          );
          setPayValidatedVoucher(v);
          setPayVoucherCode(v.code);
          if (manualDiscount > 0) {
            setPayDiscount(String(manualDiscount));
            setPayDiscountType("flat");
          }
        } catch {
          // Voucher not found/expired — just show manual discount
          if (existingDiscount > 0) {
            setPayDiscount(String(existingDiscount));
            setPayDiscountType("flat");
          }
        }
      } else if (existingDiscount > 0) {
        setPayDiscount(String(existingDiscount));
        setPayDiscountType("flat");
      }
    } catch {}
  };

  const validatePayVoucher = async () => {
    if (!payVoucherCode) return;
    try {
      const res = await api.post("/promos/vouchers/validate", {
        code: payVoucherCode,
      });
      if (res.data.valid) {
        setPayValidatedVoucher(res.data.voucher);
        toast.success("Voucher valid!");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Voucher tidak valid");
      setPayValidatedVoucher(null);
    }
  };

  const handlePay = async () => {
    const paySubtotalForCalc =
      Number(payingTx.subtotal) || Number(payingTx.total_amount);
    const baseForCalc =
      selectedSpecialPayment?.override_price != null
        ? Number(selectedSpecialPayment.override_price) * payTxServiceQty +
          payTxProductSubtotal
        : paySubtotalForCalc;
    const payManualDiscount =
      payDiscountType === "percentage"
        ? baseForCalc * (Number(payDiscount) / 100)
        : Number(payDiscount || 0);
    const payVoucherDiscount = payValidatedVoucher
      ? payValidatedVoucher.discount_type === "percentage"
        ? baseForCalc * (payValidatedVoucher.discount_value / 100)
        : Number(payValidatedVoucher.discount_value)
      : 0;
    const effectiveTotal = Math.max(
      0,
      baseForCalc - payManualDiscount - payVoucherDiscount,
    );
    if (payMethod === "cash" && Number(payAmount) < effectiveTotal) {
      toast.error("Pembayaran kurang");
      return;
    }
    try {
      const res = await api.put(`/transactions/${payingTx.id}/pay`, {
        payment_method: selectedSpecialPayment ? "special" : payMethod,
        payment_amount:
          payMethod === "cash" ? Number(payAmount) : effectiveTotal,
        special_payment_method_id: selectedSpecialPayment?.id || null,
        special_payment_override_price:
          selectedSpecialPayment?.override_price != null
            ? Number(selectedSpecialPayment.override_price)
            : null,
        discount_amount: payManualDiscount,
        voucher_id: payValidatedVoucher?.id || null,
        notes: payNotes || null,
      });
      toast.success(
        `Pembayaran berhasil!${res.data.change_amount > 0 ? ` Kembalian: ${fmt(res.data.change_amount)}` : ""}`,
      );
      setShowPayModal(false);
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal membayar");
    }
  };

  const openEditModal = async (tx) => {
    setEditingTx(tx);
    const d = parseWIB(tx.created_at);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    setEditDate(local.toISOString().slice(0, 16));
    setEditItemType("service");
    setEditItemId("");
    setEditItemQty(1);
    setEditVehicleId("");
    setEditPaymentMethod(tx.payment_method || "unpaid");
    const [sRes, pRes, txRes] = await Promise.all([
      editServices.length === 0
        ? api.get("/services").catch(() => ({ data: [] }))
        : Promise.resolve({ data: editServices }),
      editProducts.length === 0
        ? api.get("/products").catch(() => ({ data: [] }))
        : Promise.resolve({ data: editProducts }),
      api
        .get(`/transactions/${tx.id}`)
        .catch(() => ({ data: { details: [] } })),
    ]);
    const svcs =
      editServices.length === 0
        ? Array.isArray(sRes.data)
          ? sRes.data.filter((s) => s.is_active)
          : []
        : editServices;
    const prods =
      editProducts.length === 0
        ? Array.isArray(pRes.data)
          ? pRes.data.filter((p) => p.is_active)
          : []
        : editProducts;
    if (editServices.length === 0) setEditServices(svcs);
    if (editProducts.length === 0) setEditProducts(prods);
    // Load customer vehicles
    if (txRes.data?.customer_id) {
      const vRes = await api
        .get(`/customers/${txRes.data.customer_id}`)
        .catch(() => ({ data: { vehicles: [] } }));
      setEditVehicles(
        Array.isArray(vRes.data?.vehicles) ? vRes.data.vehicles : [],
      );
      if (tx.vehicle_id) setEditVehicleId(String(tx.vehicle_id));
    } else {
      setEditVehicles([]);
    }
    // Map existing details to unified list
    const details = Array.isArray(txRes.data?.details)
      ? txRes.data.details
      : [];
    setEditItems(
      details.map((d) => ({
        detailId: d.id,
        type: d.item_type,
        id: d.service_id || d.product_id,
        name: d.item_name,
        price: Number(d.unit_price),
        quantity: d.quantity,
        vehicleId: d.vehicle_id || null,
        vehiclePlate: d.vehicle_plate || null,
        isNew: false,
      })),
    );
    setShowEditModal(true);
  };

  const addEditItem = () => {
    if (!editItemId) return;
    const list = editItemType === "service" ? editServices : editProducts;
    const found = list.find((i) => i.id === Number(editItemId));
    if (!found) return;
    const chosenVehicle = editVehicles.find(
      (v) => v.id === Number(editVehicleId),
    );
    setEditItems([
      ...editItems,
      {
        detailId: null,
        type: editItemType,
        id: found.id,
        name: found.name,
        price: editItemType === "service" ? found.price : found.sell_price,
        quantity: Number(editItemQty) || 1,
        vehicleId:
          editItemType === "service" ? Number(editVehicleId) || null : null,
        vehiclePlate:
          editItemType === "service"
            ? chosenVehicle?.plate_number || null
            : null,
        isNew: true,
      },
    ]);
    setEditItemId("");
    setEditItemQty(1);
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/transactions/${editingTx.id}/edit`, {
        created_at: editDate ? new Date(editDate).toISOString() : undefined,
        payment_method: editPaymentMethod || undefined,
        items: editItems.map(
          ({ detailId, type, id, quantity, price, vehicleId }) => ({
            detailId: detailId || undefined,
            type,
            id,
            quantity,
            unit_price: price,
            vehicle_id: vehicleId || undefined,
          }),
        ),
      });
      toast.success("Transaksi berhasil diupdate");
      setShowEditModal(false);
      loadTransactions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Gagal mengupdate transaksi",
      );
    }
  };

  // Derived: total efektif di modal bayar (setelah override, diskon, voucher)
  // Gunakan subtotal (sebelum diskon) sebagai base, agar diskon yang di-prefill dihitung benar
  const paySubtotal =
    Number(payingTx?.subtotal) || Number(payingTx?.total_amount) || 0;
  const payBaseForCalc =
    selectedSpecialPayment?.override_price != null
      ? Number(selectedSpecialPayment.override_price) * payTxServiceQty +
        payTxProductSubtotal
      : paySubtotal;
  const payManualDiscountAmt =
    payDiscountType === "percentage"
      ? payBaseForCalc * (Number(payDiscount) / 100)
      : Number(payDiscount || 0);
  const payVoucherDiscountAmt = payValidatedVoucher
    ? payValidatedVoucher.discount_type === "percentage"
      ? payBaseForCalc * (payValidatedVoucher.discount_value / 100)
      : Number(payValidatedVoucher.discount_value)
    : 0;
  const payFinalTotal = Math.max(
    0,
    payBaseForCalc - payManualDiscountAmt - payVoucherDiscountAmt,
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Daftar Transaksi</h4>
        <button className="btn btn-success btn-sm" onClick={exportExcel}>
          <FiDownload className="me-1" /> Excel
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 mb-3">
        <div className="row g-2">
          {/* Search: full width on <lg, 3 cols on lg+ */}
          <div className="col-12 col-lg-3">
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Cari invoice, customer, plat nomor..."
                value={filter.search}
                style={filter.search ? { paddingRight: "2rem" } : {}}
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
              />
              {filter.search && (
                <button
                  className="btn p-0 border-0 bg-transparent text-secondary"
                  style={{
                    position: "absolute",
                    right: "0.5rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    lineHeight: 1,
                    zIndex: 2,
                  }}
                  onClick={() => clearFilter("search")}
                  title="Hapus filter"
                  tabIndex={-1}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>
          {/* Filters: full width on <lg (below search), flex row on lg+ */}
          <div className="col-12 col-lg-9">
            <div className="row g-2">
              <div className="col-6 col-lg">
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select"
                    value={filter.vehicle_type}
                    style={
                      filter.vehicle_type ? { paddingRight: "2.5rem" } : {}
                    }
                    onChange={(e) =>
                      setFilter({ ...filter, vehicle_type: e.target.value })
                    }
                  >
                    <option value="">Semua Kendaraan</option>
                    <option value="Mobil">Mobil</option>
                    <option value="Motor">Motor</option>
                    <option value="Sepeda">Sepeda</option>
                  </select>
                  {filter.vehicle_type && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("vehicle_type")}
                      title="Hapus filter"
                      tabIndex={-1}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-6 col-lg">
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select"
                    value={filter.status}
                    style={filter.status ? { paddingRight: "2.5rem" } : {}}
                    onChange={(e) =>
                      setFilter({ ...filter, status: e.target.value })
                    }
                  >
                    <option value="">Semua Status</option>
                    <option value="waiting">Waiting</option>
                    <option value="process">Process</option>
                    <option value="done">Done</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {filter.status && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("status")}
                      title="Hapus filter"
                      tabIndex={-1}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-6 col-lg">
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select"
                    value={filter.payment_method}
                    style={
                      filter.payment_method ? { paddingRight: "2.5rem" } : {}
                    }
                    onChange={(e) =>
                      setFilter({ ...filter, payment_method: e.target.value })
                    }
                  >
                    <option value="">Semua Pembayaran</option>
                    <option value="cash">Cash</option>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                    <option value="qris">QRIS</option>
                    <option value="transfer">Transfer</option>
                    <option value="unpaid">Belum Bayar</option>
                  </select>
                  {filter.payment_method && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("payment_method")}
                      title="Hapus filter"
                      tabIndex={-1}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-6 col-lg">
                <div style={{ position: "relative" }}>
                  <FiCalendar
                    size={14}
                    style={{
                      position: "absolute",
                      left: "0.6rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-muted)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                  <input
                    type="date"
                    className="form-control date-input-custom"
                    value={filter.date_from}
                    style={{
                      paddingLeft: "1.9rem",
                      paddingRight: filter.date_from ? "1.8rem" : "0.75rem",
                    }}
                    onChange={(e) =>
                      setFilter({ ...filter, date_from: e.target.value })
                    }
                  />
                  {filter.date_from && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("date_from")}
                      title="Hapus filter"
                      tabIndex={-1}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-6 col-lg">
                <div style={{ position: "relative" }}>
                  <FiCalendar
                    size={14}
                    style={{
                      position: "absolute",
                      left: "0.6rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-muted)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                  <input
                    type="date"
                    className="form-control date-input-custom"
                    value={filter.date_to}
                    style={{
                      paddingLeft: "1.9rem",
                      paddingRight: filter.date_to ? "1.8rem" : "0.75rem",
                    }}
                    onChange={(e) =>
                      setFilter({ ...filter, date_to: e.target.value })
                    }
                  />
                  {filter.date_to && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("date_to")}
                      title="Hapus filter"
                      tabIndex={-1}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Pembayaran</th>
                  <th>Status</th>
                  <th>Proses</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      <div>{tx.invoice_number}</div>
                      <small className="text-muted">
                        {fmtDt(tx.created_at)}
                      </small>
                    </td>
                    <td>
                      <div>{tx.customer_name || "-"}</div>
                      {(tx.vehicles || []).length > 0 && (
                        <div>
                          {tx.vehicles.map((v) => (
                            <div key={v.vehicle_id}>
                              <small className="text-muted">
                                {[v.brand, v.plate_number]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </small>
                              {(v.loyalty_progress || []).map((lp) => (
                                <small
                                  key={lp.rule_id}
                                  className="d-block"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {(tx.vehicles || []).length > 1 && (
                                    <span className="text-muted">
                                      [{v.plate_number}]{" "}
                                    </span>
                                  )}
                                  {lp.rule_name}:{" "}
                                  <strong>
                                    {lp.current_count}/{lp.required_count}
                                  </strong>
                                  {lp.current_count >= lp.required_count && (
                                    <Badge
                                      bg="success"
                                      className="ms-1"
                                      style={{ fontSize: "0.55rem" }}
                                    >
                                      REDEEM
                                    </Badge>
                                  )}
                                </small>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="fw-bold">{fmt(tx.total_amount)}</td>
                    <td>
                      {tx.payment_method === "unpaid" ? (
                        <Badge bg="danger">BELUM BAYAR</Badge>
                      ) : (
                        tx.payment_method?.toUpperCase()
                      )}
                    </td>
                    <td>{statusBadge(tx.status)}</td>
                    <td>
                      {/* Proses: Bayar + status transitions */}
                      <div className="d-flex gap-1 flex-wrap">
                        {tx.payment_method === "unpaid" && (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openPayModal(tx)}
                          >
                            Bayar
                          </button>
                        )}
                        {tx.status === "waiting" && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => updateStatus(tx, "process")}
                          >
                            Proses
                          </button>
                        )}
                        {tx.status === "process" && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => updateStatus(tx, "done")}
                          >
                            Selesai
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {/* Aksi: detail, edit, delete */}
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => navigate(`/transactions/${tx.id}`)}
                          title="Detail"
                        >
                          <FiEye size={14} />
                        </button>
                        {user?.role === "admin" && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openEditModal(tx)}
                            title="Edit"
                          >
                            <FiEdit2 size={14} />
                          </button>
                        )}
                        {user?.role === "admin" && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(tx)}
                            title="Hapus"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      Belum ada transaksi
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Menampilkan {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–
            {Math.min(page * PAGE_SIZE, totalCount)} dari {totalCount} transaksi
          </small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page - 1)}
                >
                  ‹
                </button>
              </li>
              {(() => {
                const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                const pages = [];
                for (let p = 1; p <= totalPages; p++) {
                  if (p === 1 || p === totalPages || Math.abs(p - page) <= 2) {
                    pages.push(p);
                  }
                }
                return pages.map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <li className="page-item disabled">
                        <span className="page-link">…</span>
                      </li>
                    )}
                    <li className={`page-item ${p === page ? "active" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </li>
                  </React.Fragment>
                ));
              })()}
              <li
                className={`page-item ${page >= Math.ceil(totalCount / PAGE_SIZE) ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page + 1)}
                >
                  ›
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Pay Modal */}
      <Modal
        show={showPayModal}
        onHide={() => setShowPayModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pembayaran - {payingTx?.invoice_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="pos-payment-total mb-3">
            <small>Total Pembayaran</small>
            <div className="pos-payment-amount">{fmt(payFinalTotal)}</div>
            {(payManualDiscountAmt > 0 || payVoucherDiscountAmt > 0) && (
              <small className="text-muted text-decoration-line-through d-block">
                {fmt(payBaseForCalc)}
              </small>
            )}
            {selectedSpecialPayment?.override_price != null && (
              <small className="text-success d-block mt-1">
                Override: {selectedSpecialPayment.name} (Rp{" "}
                {Number(selectedSpecialPayment.override_price).toLocaleString(
                  "id-ID",
                )}
                /layanan × {payTxServiceQty})
              </small>
            )}
          </div>

          <div className="pos-payment-methods mb-3">
            {["cash", "debit", "qris", "transfer"].map((m) => (
              <button
                key={m}
                className={`pos-pm-btn ${payMethod === m && !selectedSpecialPayment ? "active" : ""}`}
                onClick={() => {
                  setPayMethod(m);
                  setSelectedSpecialPayment(null);
                }}
              >
                {paymentIcons[m]}{" "}
                {m === "debit"
                  ? "Debit/Credit"
                  : m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
            {specialPaymentMethods.map((pm) => (
              <button
                key={`spm-${pm.id}`}
                className={`pos-pm-btn ${selectedSpecialPayment?.id === pm.id ? "active" : ""}`}
                onClick={() => {
                  setSelectedSpecialPayment(pm);
                  setPayMethod("special");
                }}
                title={
                  pm.override_price != null
                    ? `Override: ${fmt(pm.override_price)}`
                    : pm.name
                }
              >
                <FiDollarSign /> {pm.name}
              </button>
            ))}
          </div>

          {(!selectedSpecialPayment ||
            selectedSpecialPayment?.override_price == null) && (
            <details
              className="pos-payment-extras mb-3"
              open={!!(payDiscount || payValidatedVoucher)}
            >
              <summary>Diskon & Voucher</summary>
              <div className="pt-2">
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label small">Diskon Manual</label>
                    <div className="input-group input-group-sm">
                      <select
                        className="form-select"
                        style={{ maxWidth: "70px" }}
                        value={payDiscountType}
                        onChange={(e) => setPayDiscountType(e.target.value)}
                      >
                        <option value="flat">Rp</option>
                        <option value="percentage">%</option>
                      </select>
                      <input
                        type="number"
                        className="form-control"
                        value={payDiscount}
                        onChange={(e) => setPayDiscount(e.target.value)}
                        placeholder="0"
                        min="0"
                        max={payDiscountType === "percentage" ? 100 : undefined}
                      />
                      {payDiscount > 0 && (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setPayDiscount("")}
                          title="Hapus diskon"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                    {payDiscount > 0 && (
                      <div className="small text-muted mt-1">
                        = {fmt(payManualDiscountAmt)}
                      </div>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Kode Voucher</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        value={payVoucherCode}
                        onChange={(e) => {
                          setPayVoucherCode(e.target.value);
                          if (!e.target.value) setPayValidatedVoucher(null);
                        }}
                        placeholder="Kode"
                      />
                      {payValidatedVoucher ? (
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => {
                            setPayValidatedVoucher(null);
                            setPayVoucherCode("");
                          }}
                          title="Batalkan voucher"
                        >
                          <FiX size={14} />
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-primary"
                          onClick={validatePayVoucher}
                        >
                          Cek
                        </button>
                      )}
                    </div>
                    {payValidatedVoucher && (
                      <div className="small text-success mt-1">
                        ✓ {payValidatedVoucher.code} &mdash;{" "}
                        {payValidatedVoucher.discount_type === "percentage"
                          ? `${payValidatedVoucher.discount_value}%`
                          : fmt(payValidatedVoucher.discount_value)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </details>
          )}

          {payMethod === "cash" && !selectedSpecialPayment && (
            <div className="mb-3">
              <label className="form-label small fw-bold">Jumlah Bayar</label>
              <input
                type="number"
                className="form-control form-control-lg"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0"
                autoFocus
              />
              <div className="d-flex gap-1 mt-2 flex-wrap">
                {(() => {
                  const steps = [
                    1000, 5000, 10000, 20000, 50000, 100000, 200000, 500000,
                    1000000,
                  ];
                  const amounts = [
                    ...new Set(
                      steps.map((s) => Math.ceil(payFinalTotal / s) * s),
                    ),
                  ]
                    .filter((v) => v >= payFinalTotal)
                    .slice(0, 5);
                  return amounts.map((amt) => (
                    <button
                      key={amt}
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setPayAmount(amt.toString())}
                    >
                      {fmt(amt)}
                    </button>
                  ));
                })()}
              </div>
              {Number(payAmount) >= payFinalTotal && (
                <div className="mt-2 p-2 bg-light rounded">
                  <strong>
                    Kembalian: {fmt(Number(payAmount) - payFinalTotal)}
                  </strong>
                </div>
              )}
            </div>
          )}

          <div className="mb-2">
            <label className="form-label small">Catatan</label>
            <textarea
              className="form-control form-control-sm"
              rows="2"
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              placeholder="Opsional..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handlePay}>
            Bayar {fmt(payFinalTotal)}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit Transaksi - {editingTx?.invoice_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Date */}
          <div className="mb-3">
            <label className="form-label fw-bold">Tanggal Transaksi</label>
            <input
              type="datetime-local"
              className="form-control"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
            />
          </div>

          {/* Payment Method */}
          <div className="mb-3">
            <label className="form-label fw-bold">Metode Pembayaran</label>
            <select
              className="form-select"
              value={editPaymentMethod}
              onChange={(e) => setEditPaymentMethod(e.target.value)}
            >
              <option value="unpaid">Belum Bayar</option>
              <option value="cash">Cash</option>
              <option value="debit">Debit</option>
              <option value="credit">Credit</option>
              <option value="qris">QRIS</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>

          {/* Unified items list */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Item Transaksi
            </label>
            {editItems.length === 0 ? (
              <div className="text-muted small">Tidak ada item</div>
            ) : (
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>Kendaraan</th>
                    <th style={{ width: "80px" }}>Qty</th>
                    <th style={{ width: "120px" }}>Harga</th>
                    <th style={{ width: "130px" }}>Subtotal</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editItems.map((item, idx) => (
                    <tr key={idx} className={item.isNew ? "table-success" : ""}>
                      <td>
                        {item.name}
                        {item.isNew && (
                          <span
                            className="badge bg-success ms-1"
                            style={{ fontSize: "0.65em" }}
                          >
                            Baru
                          </span>
                        )}
                      </td>
                      <td>
                        {item.type === "service" ? (
                          <select
                            className="form-select form-select-sm"
                            value={item.vehicleId || ""}
                            onChange={(e) => {
                              const veh = editVehicles.find(
                                (v) => v.id === Number(e.target.value),
                              );
                              const updated = [...editItems];
                              updated[idx] = {
                                ...item,
                                vehicleId: Number(e.target.value) || null,
                                vehiclePlate: veh?.plate_number || null,
                              };
                              setEditItems(updated);
                            }}
                          >
                            <option value="">— pilih —</option>
                            {editVehicles.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.plate_number} ({v.vehicle_type})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.quantity}
                          min="1"
                          onChange={(e) => {
                            const updated = [...editItems];
                            updated[idx] = {
                              ...item,
                              quantity: Number(e.target.value) || 1,
                            };
                            setEditItems(updated);
                          }}
                        />
                      </td>
                      <td>{fmt(item.price)}</td>
                      <td>{fmt(item.price * item.quantity)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger py-0 px-1"
                          onClick={() =>
                            setEditItems(
                              editItems.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <FiX size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="table-light fw-bold">
                    <td colSpan="4" className="text-end">
                      Total
                    </td>
                    <td>
                      {fmt(
                        editItems.reduce((s, i) => s + i.price * i.quantity, 0),
                      )}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Add new item */}
          <div className="mb-2 border rounded p-2 bg-light">
              <label className="form-label fw-bold mb-2">Tambah Item</label>
              <div className="row g-2 align-items-end">
                <div className="col-auto">
                  <label className="form-label small mb-1">Tipe</label>
                  <select
                    className="form-select form-select-sm"
                    value={editItemType}
                    onChange={(e) => {
                      setEditItemType(e.target.value);
                      setEditItemId("");
                    }}
                  >
                    <option value="service">Layanan</option>
                    <option value="product">Produk</option>
                  </select>
                </div>
                <div className="col">
                  <label className="form-label small mb-1">
                    {editItemType === "service" ? "Layanan" : "Produk"}
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={editItemId}
                    onChange={(e) => setEditItemId(e.target.value)}
                  >
                    <option value="">-- Pilih --</option>
                    {(editItemType === "service"
                      ? editServices
                      : editProducts
                    ).map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} —{" "}
                        {fmt(
                          editItemType === "service" ? i.price : i.sell_price,
                        )}
                      </option>
                    ))}
                  </select>
                </div>
                {editItemType === "service" && editVehicles.length > 0 && (
                  <div className="col">
                    <label className="form-label small mb-1">Kendaraan</label>
                    <select
                      className="form-select form-select-sm"
                      value={editVehicleId}
                      onChange={(e) => setEditVehicleId(e.target.value)}
                    >
                      <option value="">— pilih —</option>
                      {editVehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.plate_number} ({v.vehicle_type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-auto">
                  <label className="form-label small mb-1">Qty</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "70px" }}
                    value={editItemQty}
                    min="1"
                    onChange={(e) => setEditItemQty(e.target.value)}
                  />
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1 d-block">
                    &nbsp;
                  </label>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={addEditItem}
                  >
                    <FiPlus /> Tambah
                  </button>
                </div>
              </div>
            </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleEditSave}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* WhatsApp Confirmation Modal */}
      <Modal
        show={showWAConfirm}
        onHide={() => {
          setShowWAConfirm(false);
          setDoneTx(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiSend className="me-2" />
            Kirim WhatsApp
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Transaksi <strong>{doneTx?.invoice_number}</strong> sudah selesai.
          </p>
          <p>
            Kirim notifikasi WhatsApp ke{" "}
            <strong>{doneTx?.customer_name}</strong> ({doneTx?.customer_phone})?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWAConfirm(false);
              setDoneTx(null);
            }}
          >
            Tidak
          </Button>
          <Button variant="success" onClick={sendWA} disabled={waSending}>
            {waSending ? "Mengirim..." : "Kirim WhatsApp"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel}
        confirmVariant={confirmModal.confirmVariant}
      />
    </div>
  );
};

export default Transactions;
